import React, { Component } from 'react';
import axios from 'axios';
import moment from 'moment';

import { getAuthHeader } from '../../utils/authHeader';

/**
 * AI report summary for the web Order Details page.
 *
 * Mirrors the mobile app feature so both clients behave identically:
 *   - one summary per order, cached in localStorage against a signature of the
 *     report file names (re-uploaded report -> new signature -> fresh summary)
 *   - Bangla by default with an EN toggle; the translation is cached too
 *   - daily / per-order usage shown from the server response
 *   - Print and Download use the same masthead as the invoice PDF
 *
 * Security note: this talks ONLY to the AmarLab backend. The Spreeha AI URL,
 * its credentials and its token never reach the browser — the backend proxies
 * the call server-to-server.
 */

const CACHE_PREFIX = 'alab_web_report_summary_v1_';

// `patientId`, when given, keeps each patient's cached summary separate —
// two patients on the same order must never share one cache entry.
const cacheKey = (orderId, patientId) =>
  `${CACHE_PREFIX}${orderId}${patientId ? `_p${patientId}` : ''}`;

// `patientGroup` is the { patient, reports } shape OrderDetails builds per
// patient. When given, the signature is built from just that patient's own
// report files — not every file on the order — so re-uploading one
// patient's report doesn't invalidate another patient's cached summary.
// Same idea as the app: sorted report file names identify the exact inputs.
const buildSignature = (order, patientGroup) => {
  const files = (patientGroup && Array.isArray(patientGroup.reports))
    ? patientGroup.reports
    : ((order && order.orderreport) || []);
  const names = files
    .map((r) => {
      const raw = String((r && r.report) || '');
      if (!raw) return '';
      const noQuery = raw.split('?')[0];
      return noQuery.substring(noQuery.lastIndexOf('/') + 1);
    })
    .filter(Boolean)
    .sort();
  return names.join('|');
};

const readCache = (orderId, signature, patientId) => {
  try {
    const raw = localStorage.getItem(cacheKey(orderId, patientId));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || entry.signature !== signature) return null;
    return entry;
  } catch (e) {
    return null;
  }
};

const writeCache = (orderId, entry, patientId) => {
  try {
    localStorage.setItem(cacheKey(orderId, patientId), JSON.stringify(entry));
  } catch (e) {
    /* best effort */
  }
};

const escapeHtml = (s) =>
  String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const getPatientName = (order, patientGroup) => {
  try {
    if (patientGroup && patientGroup.patient && patientGroup.patient.full_name) {
      return patientGroup.patient.full_name;
    }
    const items = (order && order.orderitem) || [];
    for (let i = 0; i < items.length; i += 1) {
      const n = items[i] && items[i].patient && items[i].patient.full_name;
      if (n) return n;
    }
    const u = order && order.user;
    if (u) {
      const full = `${u.first_name || ''} ${u.last_name || ''}`.trim();
      if (full) return full;
      if (u.username) return u.username;
    }
  } catch (e) { /* ignore */ }
  return '';
};

const getContact = (order, patientGroup) => {
  try {
    if (patientGroup && patientGroup.patient && patientGroup.patient.mobile) {
      return patientGroup.patient.mobile;
    }
    const items = (order && order.orderitem) || [];
    for (let i = 0; i < items.length; i += 1) {
      const m = items[i] && items[i].patient && items[i].patient.mobile;
      if (m) return m;
    }
    return (order && order.user && order.user.username) || '';
  } catch (e) {
    return '';
  }
};

// {orderId}_{patient}_{date}_report_summarize
const buildFileName = (order, patientGroup) => {
  const orderId = (order && order.order_id) || 'order';
  const patient = (getPatientName(order, patientGroup) || 'patient')
    .replace(/[^\wঀ-৿]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const date = moment().format('YYYY_MM_DD');
  return `${orderId}_${patient}_${date}_report_summarize`;
};

// Same masthead + info table as the invoice, so both documents match.
const buildHtml = (summaryText, order, patientGroup) => {
  const orderId = (order && order.order_id) || '-';
  const patientName = getPatientName(order, patientGroup) || '-';
  const contact = getContact(order, patientGroup) || '-';
  const orderDate = order && order.created_at
    ? moment(order.created_at).format('DD MMM YYYY; hh:mm A')
    : '-';

  const body = escapeHtml(summaryText)
    .split('\n')
    .map((raw) => {
      const line = raw.trim();
      if (!line) return '<div style="height:8px"></div>';
      const heading = line.match(/^\*\*(.+?)\*\*:?$/);
      if (heading) return `<h3>${heading[1]}</h3>`;
      const withBold = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      if (/^[-•*]\s+/.test(line)) {
        return `<li>${withBold.replace(/^[-•*]\s+/, '')}</li>`;
      }
      return `<p>${withBold}</p>`;
    })
    .join('\n');

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(buildFileName(order, patientGroup))}</title>
    <style>
      @page { margin: 40px 45px; }
      body { background:#FFF; font-family: Georgia, serif; color:#323232; margin:0; padding:18px 22px; font-size:14px; }
      .masthead { display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; padding:0 5px; }
      .logo-box { width:90px; height:90px; display:flex; justify-content:center; align-items:center; }
      .logo-box img { width:90%; height:90%; object-fit:contain; }
      .title-box { margin:0 10px; display:flex; flex-direction:column; align-items:center; justify-content:center; }
      .brand { font-size:40px; font-weight:500; }
      .subtitle { font-size:12px; }
      .rule { background-color:darkslategray; height:1px; opacity:1; }
      .info_tbl { width:100%; border-collapse:collapse; margin-top:14px; font-size:13px; }
      .info_tbl td { padding:3px 4px; vertical-align:top; }
      h3 { font-size:14px; margin:14px 0 4px; color:#1C1C1C; }
      p { margin:4px 0; line-height:1.6; }
      li { margin:3px 0 3px 18px; line-height:1.6; }
      .section-title { font-size:16px; font-weight:600; margin:18px 0 6px; }
      .footer { margin-top:26px; padding-top:10px; border-top:1px solid #E5E7EB; color:#6B7280; font-size:10px; line-height:1.5; }
    </style>
  </head>
  <body>
    <div class="masthead">
      <div class="logo-box"><img src="https://i.ibb.co/fxwrZ7g/logo.png" /></div>
      <div class="title-box">
        <div class="brand">Amarlab</div>
        <div class="subtitle">Report Summary — ${moment().format('DD MMM YYYY')}</div>
      </div>
    </div>
    <div class="rule"></div>
    <table class="info_tbl">
      <tr>
        <td>Order ID</td><td>: #${escapeHtml(orderId)}</td>
        <td>Patient Name</td><td>: ${escapeHtml(patientName)}</td>
      </tr>
      <tr>
        <td>Order Date</td><td>: ${escapeHtml(orderDate)}</td>
        <td>Contact</td><td>: ${escapeHtml(contact)}</td>
      </tr>
    </table>
    <div class="rule" style="margin-top:14px;"></div>
    <div class="section-title">AI Report Summary</div>
    ${body}
    <div class="footer">
      This summary is generated by AI for information only. It is not a medical
      diagnosis and does not replace the advice of a qualified physician.
    </div>
  </body>
</html>`;
};

// Renders the AI's markdown (bold headings, "-" bullets, numbered sections).
const FormattedSummary = ({ text }) => {
  const renderInline = (line, keyPrefix) => {
    const parts = String(line).split(/(\*\*[^*]+\*\*)/g);
    return parts.filter(Boolean).map((part, i) => {
      const m = part.match(/^\*\*([^*]+)\*\*$/);
      if (m) return <strong key={`${keyPrefix}-b${i}`}>{m[1]}</strong>;
      return <span key={`${keyPrefix}-t${i}`}>{part}</span>;
    });
  };

  const lines = String(text || '').split('\n');
  return (
    <div className='ai-summary-body'>
      {lines.map((raw, i) => {
        const line = raw.trim();
        if (!line) return <div key={`sp-${i}`} style={{ height: 8 }} />;

        const heading = line.match(/^\*\*(.+)\*\*:?$/);
        if (heading) {
          return (
            <h6 key={`h-${i}`} style={{ marginTop: 14, marginBottom: 4, fontWeight: 700, fontSize: 15 }}>
              {heading[1]}
            </h6>
          );
        }
        if (/^[-•*]\s+/.test(line)) {
          return (
            <div key={`li-${i}`} style={{ display: 'flex', marginBottom: 4 }}>
              <span style={{ color: '#31B5C4', marginRight: 8 }}>•</span>
              <span style={{ flex: 1, lineHeight: 1.7 }}>
                {renderInline(line.replace(/^[-•*]\s+/, ''), `li-${i}`)}
              </span>
            </div>
          );
        }
        if (/^[০-৯\d]+[.)]\s+/.test(line)) {
          return (
            <p key={`n-${i}`} style={{ marginTop: 12, marginBottom: 3, fontWeight: 600, lineHeight: 1.7 }}>
              {renderInline(line, `n-${i}`)}
            </p>
          );
        }
        return (
          <p key={`p-${i}`} style={{ margin: '4px 0', lineHeight: 1.7 }}>
            {renderInline(line, `p-${i}`)}
          </p>
        );
      })}
    </div>
  );
};

class ReportSummaryModal extends Component {
  state = {
    loading: false,
    error: '',
    retryHint: '',
    summaryBn: '',
    summaryEn: '',
    language: 'bn',
    translating: false,
    quota: null,
    fromCache: false,
    copied: false,
  };

  componentDidMount() {
    if (this.props.show) this.loadSummary();
  }

  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) {
      this.loadSummary();
    }
    if (!this.props.show && prevProps.show) {
      this.setState({
        loading: false, error: '', retryHint: '', summaryBn: '', summaryEn: '',
        language: 'bn', translating: false, quota: null, fromCache: false,
      });
    }
  }

  authHeaders = () => getAuthHeader() || {};

  loadSummary = async (force = false) => {
    const { order, patient } = this.props;
    if (!order) return;

    this.setState({ loading: true, error: '', retryHint: '' });
    const signature = buildSignature(order, patient);
    const patientId = patient && patient.patient && patient.patient.id;

    // 1) Device cache — same order + same patient + same report files, so
    // nothing to pay for.
    if (!force) {
      const cached = readCache(order.id, signature, patientId);
      if (cached && cached.summary) {
        this.setState({
          summaryBn: cached.summary,
          summaryEn: cached.translation || '',
          quota: cached.quota || null,
          fromCache: true,
          loading: false,
        });
        return;
      }
    }

    // 2) Ask the backend (this is what consumes a use).
    await this.loadSummaryBlocking(order, signature, patient);
  };

  // Non-streaming fallback — the original blocking call, used when the browser
  // has no ReadableStream support or the stream errors mid-flight.
  loadSummaryBlocking = async (order, signature, patient) => {
    const patientId = patient && patient.patient && patient.patient.id;
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/order/report-summarize/`,
        patientId ? { order: order.id, patient: patientId } : { order: order.id },
        { headers: this.authHeaders() }
      );
      const data = resp.data || {};
      const text = data.summarization || '';
      const quota = {
        remaining: data.remaining,
        daily_limit: data.daily_limit,
        order_remaining: data.order_remaining,
        per_order_limit: data.per_order_limit,
      };
      this.setState({ summaryBn: text, summaryEn: '', quota, fromCache: false, loading: false });
      writeCache(order.id, { signature, summary: text, translation: '', quota, cachedAt: Date.now() }, patientId);
    } catch (err) {
      const data = (err.response && err.response.data) || {};
      this.setState({
        loading: false,
        error: data.detail || 'Could not generate the summary. Please try again.',
        retryHint: '',
        quota: typeof data.remaining === 'number'
          ? {
            remaining: data.remaining,
            daily_limit: data.daily_limit,
            order_remaining: data.order_remaining,
            per_order_limit: data.per_order_limit,
          }
          : this.state.quota,
      });
    }
  };

  switchLanguage = async (lang) => {
    const { order, patient } = this.props;
    const { language, summaryBn, summaryEn } = this.state;
    if (lang === language) return;

    if (lang === 'bn') {
      this.setState({ language: 'bn' });
      return;
    }
    if (summaryEn) {
      this.setState({ language: 'en' });
      return;
    }
    if (!summaryBn) return;

    this.setState({ translating: true });
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/order/report-translate/`,
        { text: summaryBn },
        { headers: this.authHeaders() }
      );
      const translated = (resp.data && resp.data.translated_text) || '';
      if (!translated) throw new Error('empty');

      this.setState({ summaryEn: translated, language: 'en', translating: false });

      const patientId = patient && patient.patient && patient.patient.id;
      const signature = buildSignature(order, patient);
      const existing = readCache(order.id, signature, patientId) || {};
      writeCache(order.id, {
        ...existing,
        signature,
        summary: summaryBn,
        translation: translated,
        quota: existing.quota || this.state.quota || null,
        cachedAt: existing.cachedAt || Date.now(),
      }, patientId);
    } catch (e) {
      this.setState({ translating: false });
    }
  };

  shownText = () =>
    (this.state.language === 'en' ? this.state.summaryEn : this.state.summaryBn);

  copySummary = () => {
    const text = this.shownText();
    if (!text) return;
    const done = () => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1500);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {});
    } else {
      // Fallback for older browsers / non-secure contexts.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); done(); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    }
  };

  handlePrint = () => {
    const text = this.shownText();
    if (!text) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(buildHtml(text, this.props.order, this.props.patient));
    w.document.close();
    w.focus();
    // Give the logo a moment to load so it appears in the printout.
    setTimeout(() => {
      w.print();
    }, 400);
  };

  render() {
    const { show, onClose, order, patient } = this.props;
    const {
      loading, error, retryHint, summaryBn, language, translating, quota, fromCache,
    } = this.state;

    if (!show) return null;
    const text = this.shownText();
    const patientName = getPatientName(order, patient);

    return (
      <div
        className='ai-summary-backdrop'
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#FFF', borderRadius: 8, width: '100%', maxWidth: 760,
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '16px 20px', borderBottom: '1px solid #F0FAFB',
            }}
          >
            <div style={{ flex: 1 }}>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: 17 }}>Report Summary</h5>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                Order #{order ? order.order_id : '-'}
                {patientName ? `  •  ${patientName}` : ''}
              </div>
            </div>

            {/* Language chips */}
            {!!summaryBn && !loading && !error && (
              <div style={{ display: 'flex', gap: 6 }}>
                {[{ code: 'bn', label: 'বাংলা' }, { code: 'en', label: 'EN' }].map((c) => {
                  const active = language === c.code;
                  return (
                    <button
                      key={c.code}
                      type='button'
                      onClick={() => this.switchLanguage(c.code)}
                      disabled={translating}
                      style={{
                        border: `1px solid ${active ? '#23818C' : '#CBD5E1'}`,
                        background: active ? '#23818C' : '#FFF',
                        color: active ? '#FFF' : '#4C4A4A',
                        borderRadius: 12, padding: '3px 12px', fontSize: 12,
                        fontWeight: 600, cursor: translating ? 'not-allowed' : 'pointer',
                        opacity: translating ? 0.6 : 1,
                      }}
                    >
                      {translating && c.code === 'en' && !active ? '...' : c.label}
                    </button>
                  );
                })}
              </div>
            )}

            <button
              type='button'
              onClick={onClose}
              style={{
                border: 'none', background: 'transparent', fontSize: 26,
                lineHeight: 1, cursor: 'pointer', color: '#2D2D32', padding: '0 4px',
              }}
              aria-label='Close'
            >
              &times;
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
            {loading && (
              <div style={{ textAlign: 'center', padding: '50px 16px' }}>
                <div className='spinner-border text-info' role='status' />
                <p style={{ marginTop: 14, color: '#4C4A4A', fontSize: 14 }}>
                  Analysing your report... this can take a moment.
                </p>
              </div>
            )}

            {!loading && !!error && (
              <div style={{ textAlign: 'center', padding: '40px 16px' }}>
                <p style={{ color: '#D14343', fontSize: 14, marginBottom: 4 }}>{error}</p>
                {!!retryHint && (
                  <p style={{ color: '#6B7280', fontSize: 13, fontWeight: 600 }}>{retryHint}</p>
                )}
                <button
                  type='button'
                  className='btn btn-outline-info'
                  style={{ marginTop: 12 }}
                  onClick={() => this.loadSummary(true)}
                >
                  Try again
                </button>
              </div>
            )}

            {!loading && !error && !!text && <FormattedSummary text={text} />}
          </div>

          {/* Footer */}
          {!loading && !error && !!text && (
            <div style={{ borderTop: '1px solid #F0FAFB', padding: '12px 20px 16px' }}>
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 10, flexWrap: 'wrap', gap: 6,
                }}
              >
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  {quota && typeof quota.remaining === 'number'
                    ? `${quota.remaining} left today`
                    : ''}
                  {quota && typeof quota.order_remaining === 'number'
                    ? `  •  ${quota.order_remaining} left for this order`
                    : ''}
                </span>
                {fromCache && (
                  <span
                    style={{
                      fontSize: 11, color: '#1B9C56', background: '#E9F7EF',
                      borderRadius: 10, padding: '2px 8px', fontWeight: 600,
                    }}
                  >
                    Saved
                  </span>
                )}
              </div>

              {/* Print only. A "Download" here could only produce an .html
                  file (there is no PDF library on the web project that can
                  render Bangla), which is not a useful file for a patient.
                  The print dialog offers "Save as PDF" and the document title
                  is already set to the correct file name. */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type='button'
                  className='btn btn-outline-info'
                  style={{ flex: '0 0 auto', fontWeight: 600 }}
                  onClick={this.copySummary}
                  title='Copy summary'
                >
                  {this.state.copied ? '✓ Copied' : '⧉ Copy'}
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  style={{ flex: 1 }}
                  onClick={this.handlePrint}
                >
                  Print / Save as PDF
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ReportSummaryModal;
